import React from 'react';
import Button from '../button/Button';
import { useNavigate } from 'react-router';

type PricingCardProps = {
  title: string;
  buttonTitle: string;
  setupFee: string;
  previousSetupFee?: string;
  monthlyFee: number | string;
  monthlyFeeText?: string;
  features: {
    domain: string;
    referralLinkAlias?: string;
    countriesLanguages: string;
    SingleSignOnIntegration: boolean;
    CustomAPIsAndIntegrations: boolean;
    enrolled: { cards: string; additionalCards: string } | string;
    forUpEnrolledCards: string | null;
    newsletters: string;
    support: string;
    cashbackFrequency: string;
    standoutFeatures: string | JSX.Element;
    appMode: string;
  };
  isMostPopular?: boolean;
  bannerText?: string;
  showFullFeatures: boolean;
};

function canBeNumber(value: string | number) {
  if (typeof value === 'string') {
    const sanitizedValue = value.replace(/,/g, '');
    return !isNaN(Number(sanitizedValue));
  }
  return false;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  setupFee,
  previousSetupFee,
  monthlyFee,
  features,
  showFullFeatures,
  buttonTitle,
  bannerText,
  monthlyFeeText,
}) => {
  const navigate = useNavigate();

  return (
    <div
      className={`w-full sm:max-w-[18.1rem] px-6 pt-8 bg-white sm:rounded-lg overflow-hidden text-left flex flex-col ${
        showFullFeatures ? 'pb-16' : 'pb-7'
      }`}
    >
      <div className='relative'>
        {bannerText && (
          <div
            className={`absolute -top-[15px] -right-10 px-6 py-1  text-base font-semibold rounded-full h-10 ${
              bannerText !== 'Limited Time Offer'
                ? 'bg-white text-secondary border-2 border-secondary'
                : 'bg-primary text-white border-2 border-primary'
            }`}
            style={{ whiteSpace: 'nowrap' }}
          >
            <p className='text-center leading-none flex items-center justify-center h-full'>
              {bannerText}
            </p>
          </div>
        )}
      </div>
      <div className='flex-1 flex flex-col'>
        <h3 className='text-2xl font-extrabold mb-4 mt-10'>{title}</h3>
        <div className='flex items-center gap-4'>
          {previousSetupFee && (
            <p
              className={`mt-4 mb-2 text-2xl line-through text-gray-500 font-semibold`}
            >
              {`${canBeNumber(previousSetupFee) ? `$${previousSetupFee}` : previousSetupFee}`}
            </p>
          )}
          <p
            className={`mt-4 mb-2 text-xl sm:text-nowrap ${
              canBeNumber(setupFee)
                ? 'font-extrabold'
                : 'font-semibold text-gray-600'
            }`}
          >
            {`${canBeNumber(setupFee) ? `$${setupFee}` : setupFee}`}
          </p>
        </div>
        {features.enrolled !== 'Custom' && (
          <p className='text-gray-500 font-semibold'>One-time setup fee</p>
        )}
        <div className='mt-8'>
          {monthlyFee && (
            <p className={`text-2xl font-extrabold`}>
              {`${canBeNumber(monthlyFee) ? `$${monthlyFee}` : monthlyFee}`}
            </p>
          )}
          <p
            className={`text-gray-500 font-semibold text-sm ${monthlyFee ? 'mt-2' : ''} mb-3`}
          >
            {monthlyFeeText ? monthlyFeeText : 'Monthly service fee'}
          </p>
          {features.forUpEnrolledCards && (
            <p className='text-gray-500 font-semibold text-sm'>
              {canBeNumber(features.forUpEnrolledCards) ||
              features.forUpEnrolledCards.length < 20
                ? `For up to ${features.forUpEnrolledCards} enrolled cards`
                : features.forUpEnrolledCards}
            </p>
          )}
        </div>
      </div>
      {buttonTitle ? (
        <Button
          className={`w-[13.3rem] sm:w-full text-[#00C389] border-[2px] border-[#00C389] text-sm font-bold mt-6 mb-10`}
          onClick={() => {
            if (buttonTitle === 'Get Started') {
              navigate('/signup');
            } else if (buttonTitle === 'Contact Us') {
              navigate('/#contact-form');
            }
          }}
        >
          {buttonTitle}
        </Button>
      ) : (
        <div
          className={`${
            features.enrolled === 'Custom'
              ? 'mt-28 sm:mt-20 mb-10'
              : 'mt-6 mb-10'
          } h-12`}
        ></div>
      )}
      {showFullFeatures && (
        <div className='mt-6'>
          <h4 className='text-lg font-extrabold text-[#00C389]'>
            {title} includes:
          </h4>
          <ul className='mt-8 space-y-3'>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>Monthly Card Fee:</p>
                {features.enrolled !== 'Custom' ? (
                  <p className='text-gray-500 font-semibold text-sm'>
                    {title === 'Enterprise' ? (
                      '$250/m for each bracket of 5,000 cards'
                    ) : (
                      <>
                        Up to{' '}
                        {typeof features.enrolled === 'object' &&
                          features.enrolled.cards}{' '}
                        cards, additional{' '}
                        {typeof features.enrolled === 'object' &&
                          features.enrolled.additionalCards}{' '}
                        cards +$250/m
                      </>
                    )}
                  </p>
                ) : (
                  <p className='text-gray-500 font-semibold text-sm pb-5'>
                    {features.enrolled}
                  </p>
                )}
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>App Mode:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.appMode}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-9'>
                <p className='font-bold text-sm'>Countries & Languages:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.countriesLanguages
                    .split('\n')
                    .map((line, index) => (
                      <span key={index}>
                        {line}
                        {index <
                          features.countriesLanguages.split('\n').length -
                            1 && <br />}
                      </span>
                    ))}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div
                className={`space-y-3 ${
                  features.support.length < 30 ? 'mb-9' : 'mb-4'
                }`}
              >
                <p className='font-bold text-sm'>Partner support:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.support}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>Domain:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.domain}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>Referral Link Alias:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.referralLinkAlias}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>Single Sign-On Integration:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.SingleSignOnIntegration ? 'Yes' : 'No'}
                </p>
              </div>
            </li>
            <li className='h-[82px]'>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>
                  Custom API's and Integration:
                </p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.CustomAPIsAndIntegrations ? 'Yes' : 'No'}
                </p>
              </div>
            </li>

            {/* <li>
              <div className='space-y-3 mb-5'>
                <p className='font-bold text-sm'>Promotional newsletters:</p>
                <p className='text-gray-500 font-semibold text-sm'>
                  {features.newsletters}
                </p>
              </div>
            </li> */}

            {features.cashbackFrequency && (
              <li className='h-[82px]'>
                <div className='space-y-3'>
                  <p className='font-bold text-sm'>
                    Cashback payout frequency:
                  </p>
                  <p className='text-gray-500 font-semibold text-sm'>
                    {features.cashbackFrequency}
                  </p>
                </div>
              </li>
            )}
          </ul>
          {features.standoutFeatures && (
            <>
              <p className='mt-6 text-[#00C389] font-semibold text-base'>
                Standout features:
              </p>
              <p className='text-gray-900 font-bold text-sm mt-5'>
                {features.standoutFeatures}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PricingCard;
